import React, { useEffect, useId, useState, ChangeEvent } from "react";
import {
  Button,
  Input,
  Label,
  Select,
  Toaster,
} from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import OverlayLoader from "../../../components/OverlayLoader";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import TextEditor from "../../../components/TextEditor";
import apiServices from "../../../service";
import { stripHtml } from "../../../utils";
import { setDialogModalOptions } from "../../../redux/modelDialogReducer";
import ModalDialog from "../../../components/ModalDialog";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectField {
  field: string;
  options: SelectOption[];
}

const DistributionForm = (props: any) => {
  const selectId = useId();
  const { showToast, toasterId } = useToastMessage();
  const dispatch: AppDispatch = useDispatch();

  // Convert status to number for easier logic
  const status = Number(props.data?.status);
  const isEditable = status === 0 || status === 1;
  // const isEditable = true

  const getStatusLabel = (status: string | number) => {
    switch (String(status)) {
      case "1":
        return "In Payment";
      case "0":
        return "Not Started";
      case "-1":
        return "Stopped";
      case "-2":
        return "Fully Paid";
      default:
        return "";
    }
  };

  // useEffect(() => {
  //   if (!props?.data?.commissionAllocationId) return;
  //   const res = apiServices.distribution.getDetails(props?.data?.commissionAllocationId);
  //   res.then((response: any) => {
  //     if (response.data && response.data.data) {
  //       const distributionDetails = response.data.data;
  //       console.log("distributionDetails", distributionDetails);
  //     }
  //   });
  // }, [props?.data?.commissionAllocationId]);


  const dialogModalOptions = useSelector((state: RootState) => state.dialog);
  console.log("props.data", props.data)

  const [loading, setLoading] = useState(false);
  const [formField, setFormField] = useState({
    status: getStatusLabel(props.data?.status) || "",
    current_rule: props.data?.current_rule || "",
    party_type: props.data?.partyType || "",
    case_type: props.data?.caseType || "",
    party_name: props.data?.partyName || "",
    amount: props.data?.receivedAmount || "0.00",
    commission_id: props.data?.commissionId || "",
    commission_rule_id: props.data?.commissionRuleId || "",
    commission_allocation_id: props.data?.commissionAllocationId || "",
    note_text: props.data?.note || "",
  });
  const [originalField, setOriginalField] = useState<any>(null);

  const [getRuleId, setGetRuleId] = useState<{ [key: string]: any }>({});
  function ruleId(id: { [key: string]: any }) {
    setGetRuleId(id);
  }

  useEffect(() => {
    if (getRuleId && Object.keys(getRuleId).length > 0) {
      setFormField((prev) => ({
        ...prev,
        case_type: getRuleId.case_type || prev.case_type,
        party_type: getRuleId.party_type || prev.party_type,
        party_name: getRuleId.party_name || prev.party_name,
        note_text: getRuleId.note_text || prev.note_text,
        amount: getRuleId.amount || prev.amount,
        commission_rule_id: getRuleId.rule_id || prev.commission_rule_id,
        commission_allocation_id: getRuleId.id || prev.commission_allocation_id,
      }));
    }
  }, [getRuleId]);

  useEffect(() => {
    if (props.data && !originalField) {
      const initialValues = {
        party_type: props.data?.partyType || "",
        party_name: props.data?.partyName || "",
        amount: props.data?.receiptAmount || "0.00",
        commission_rule_id: props.data?.commissionRuleId || "",
        note_text: props.data?.note || "",
      };
      setOriginalField(initialValues);
    }
  }, [props.data]);

  const hasChanges = () => {
    if (!originalField) return false;
    const currentNote = stripHtml(formField.note_text).trim();
    const originalNote = stripHtml(originalField.note_text).trim();

    return (
      formField.party_type !== originalField.party_type ||
      formField.party_name !== originalField.party_name ||
      formField.amount !== originalField.amount ||
      formField.commission_rule_id !== originalField.commission_rule_id ||
      currentNote !== originalNote
    );
  };

  // Always initialize as array
  const [selectFieldOptions, setSelectFieldOptions] = useState<SelectField[]>(
    []
  );

  /** Fetch list options for dropdowns */
  const getSelectionListOptions = async () => {
    try {
      const fieldLbl =
        "commission_party_type:FixedList,party_name:MasterDatawithNewEntry,true_false:FixedList";
      const res = await apiServices.selectionList.getOptions(fieldLbl);

      if (res.data && res.data.data) {
        const responseData = res.data.data;

        // Normalize response: always return array of { field, options }
        let formattedOptions: SelectField[] = [];

        if (Array.isArray(responseData)) {
          formattedOptions = responseData;
        } else if (typeof responseData === "object") {
          formattedOptions = Object.keys(responseData).map((key) => ({
            field: key,
            options: responseData[key],
          }));
        }

        setSelectFieldOptions(formattedOptions);
      } else {
        setSelectFieldOptions([]);
      }
    } catch (error) {
      console.error("Error fetching selection options:", error);
      showToast("Failed to load dropdown options", "", "error");
      setSelectFieldOptions([]);
    }
  };

  useEffect(() => {
    getSelectionListOptions();
  }, []);

  /** Handle input change */
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormField((prev) => ({ ...prev, [name]: value }));
  };

  /** Handle submit */
  const handleUpdateDistribution = async () => {
    setLoading(true);
    try {
      const sanitizedNote = formField.note_text
        ? stripHtml(formField.note_text).trim()
        : "";

      const updateValue = {
        commission_rule_id: formField.commission_rule_id,
        party_type: formField.party_type,
        party_name: formField.party_name,
        amount: formField.amount,
        note: sanitizedNote,
      };

      const response = await apiServices.distribution.update(
        formField.commission_allocation_id,
        updateValue
      );

      if (response.data.isError) {
        const errorMessage = Array.isArray(
          response.data.error?.message || response.data.message
        )
          ? response.data.error.message.join(", ")
          : response.data.error?.message;

        showToast(errorMessage, "", "error");
      } else if (response?.data?.message) {
        showToast(response?.data?.message, "", "error");
      }

      setTimeout(() => {
        props.setReceiptEditpopup(false);
      }, 2000);
    } catch (error: any) {
      console.error("Update failed:", error);
      showToast("Failed to update distribution", "", "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper to get options by field
  const getOptionsByField = (fieldName: string): SelectOption[] => {
    const field = selectFieldOptions.find((opt) => opt.field === fieldName);
    return field?.options || [];
  };

  return (
    <div>
      <OverlayLoader isLoading={loading} />
      <ModalDialog options={dialogModalOptions} />
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        timeout={1000}
      />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          appearance="secondary"
          size="medium"
          disabled={!isEditable}
          onClick={() => {
            dispatch(
              setDialogModalOptions({
                open: true,
                rule: true,
                ruleId: ruleId,
                getRuleId: getRuleId,
                cancel: {
                  onclick: () => {
                    dispatch(setDialogModalOptions({ open: false }));
                  },
                  label: "Cancel",
                },
                no: {
                  onclick: () => {
                    dispatch(setDialogModalOptions({ open: false }));
                  },
                  label: "Select",
                  type: "primary",
                },
              })
            );
          }}
        >
          Choose Distribution Rule
        </Button>
      </div>

      <div className="commission_action_main">
        {/* Status */}
        <Label className="settings-table-content-label">Status</Label>
        <Input name="status" value={formField.status} readOnly disabled />

        {/* Current Rule */}
        <Label className="settings-table-content-label">Current Rule</Label>
        <Input
          name="current_rule"
          value={formField.current_rule}
          readOnly
          disabled
        />

        {/* Party Type */}
        <Label className="settings-table-content-label">Party Type</Label>
        <Select
          id={`${selectId}-party-type`}
          disabled={!isEditable}
          name="party_type"
          value={formField.party_type}
          onChange={(e) =>
            setFormField({ ...formField, party_type: e.target.value })
          }
        >
          <option value="">Select...</option>
          {getOptionsByField("commission_party_type").map((opt, index) => (
            <option
              key={opt?.value ?? `commission_party_type-${index}`}
              value={opt.value}
            >
              {opt.label}
            </option>
          ))}
        </Select>

        {/* Consultant */}
        <Label className="settings-table-content-label">Consultant</Label>
        <Input
          name="consultant"
          // value={formField?.consultant}
          readOnly
          disabled
        />

        {/* Case Type */}
        <Label className="settings-table-content-label">Case Type</Label>
        <Input name="case_type" value={formField.case_type} readOnly disabled />

        {/* Party Name */}
        <Label className="settings-table-content-label">Party Name</Label>
        <Select
          id={`${selectId}-party-name`}
          name="party_name"
          value={formField.party_name}
          disabled={!isEditable}
          onChange={(e) =>
            setFormField({ ...formField, party_name: e.target.value })
          }
        >
          <option value="">Select...</option>
          {getOptionsByField("party_name").map((opt, index) => (
            <option key={opt?.value ?? `party_name-${index}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>

        {/* Amount */}
        <Label className="settings-table-content-label">Amount</Label>
        <Input
          type="number"
          name="amount"
          value={formField.amount}
          readOnly
          onChange={onChange}
          disabled={!isEditable}
        />

        {/* Rule ID */}
        <Label className="settings-table-content-label">Rule ID</Label>
        <Input
          name="commission_rule_id"
          value={formField.commission_rule_id}
          readOnly
          disabled
        />

        {/* Note */}
        <Label className="settings-table-content-label">Note</Label>
        <TextEditor
          inputData={formField.note_text}
          disabled={!isEditable}
          onChange={(text: string) => {
            const cleanedText = stripHtml(text).trim();
            const sanitizedText =
              /^(<p>\s*<br>\s*<\/p>|\s*)$/i.test(text.trim()) ||
                cleanedText === ""
                ? ""
                : text;
            setFormField((prev) => ({ ...prev, note_text: sanitizedText }));
          }}
        />
      </div>

      <div className="button-end-popup">
        {(status === 0 || status === 1) && (
          <Button
            shape="rounded"
            onClick={() => props?.setStopDistributionConfirmationPopup(true)}
            style={{ marginRight: 6 }}
          >
            Stop Distribution
          </Button>
        )}
        <Button
          shape="rounded"
          appearance="secondary"
          size="medium"
          style={{ marginRight: 6 }}
          onClick={handleUpdateDistribution}
          disabled={loading || !isEditable || !hasChanges()}
        >
          Save
        </Button>

        <Button
          shape="rounded"
          appearance="primary"
          className="asc-button-primary"
          size="medium"
          onClick={() => props.setReceiptEditpopup(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default DistributionForm;
